import { Error as CwbApiError } from "@amzn/client-workbench-api-model";
import {
  Button,
  ButtonIconPosition,
} from "@amzn/stencil-react-components/button";
import {
  Checkbox,
  CheckboxColorScheme,
} from "@amzn/stencil-react-components/checkbox";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import {
  Link,
  LinkTextDecorationOptions,
} from "@amzn/stencil-react-components/link";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { H3, Label, Text } from "@amzn/stencil-react-components/text";
import IconExternalLinkSmall from "@amzn/stencil-react-icons/icons/icon-external-link-small";
import { trackStructEvent } from "@snowplow/browser-tracker";
import { AxiosResponse } from "axios";
import { useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { UseMutateFunction } from "react-query";

import midwayWelcomeMessage from "@/assets/images/midwayWelcomeMessage.jpg";
import securityKeyCreateNewPin from "@/assets/images/securityKeyCreateNewPin.jpg";
import securityKeyRegisterKey from "@/assets/images/securityKeyGetStarted.jpg";
import securityKeyGetStartedOtp from "@/assets/images/securityKeyGetStartedOtp.jpg";
import securityKeyKeyboardSetup from "@/assets/images/securityKeyKeyboardSetup.png";
import securityKeySuccess from "@/assets/images/securityKeySuccess.jpg";
import tempPinLogin from "@/assets/images/tempPinLogin.png";
import { AccountIssue } from "@/components/Commons/ErrorBanner/AccountIssue";
import ImagesRow from "@/components/Commons/ImagesRow";
import { SendPinError } from "@/components/RegisterSecurityKey/SendPinError";
import { getOS, OPERATING_SYSTEM } from "@/helpers/os-helper";
import { useSendTempPin } from "@/hooks/useSendTempPin";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

import { ActionType, AppContext } from "../../stores";
import {
  createSideBySidePopup,
  resizeWizardForSideBySide,
} from "../Commons/popup-helper";
import { StepBodyStyledText } from "../Commons/StepBodyStyledText";
import Video from "../Commons/Video";
import {
  ITS_LIGHT_TEAL,
  STEP_ID,
  VideoGuidanceName,
  VIDEOS_PLAYBACK_CONFIGS,
} from "../constants";

/**
 * Passed to SendTempPinStepView from RegisterSecurityKey() in order to have same reference to the useSendTempPin hook
 * tempPinSent (isSuccess) is used for both the send pin error and the disabled status of the register key on Midway button
 */
type SendTempPinStepViewProps = {
  tempPinError?: AxiosResponse<CwbApiError, any>;
  tempPinLoading: boolean;
  tempPinSent: boolean;
  sendTempPin: UseMutateFunction<void, unknown, unknown, unknown>;
};

/**
 * Returns true if the error is non-retryable, or false if it allows another PIN request.
 * Note that the 429 in this case is treated as non-retryable because it is a maximum attempts allowed, not a rate limiting
 * See BE source code: https://code.amazon.com/packages/ClientWorkbenchLambda/blobs/0478084d3816ba61f3d0d7269953cd0478514079/--/src/api/user/send-midway-temp-pin.ts#L75-L81
 * @param error
 */
const isNonRetryableError = (
  error: AxiosResponse<CwbApiError, any> | undefined
): boolean => {
  // If error is undefined, it is retryable
  if (!error) return false;
  // If error data is undefined, return whether or not it is a rate limiting error
  if (!error.data?.supplementalStatusCode) return error.status === 429;

  // Otherwise, check the non-retryable status codes (from Midway API responses)
  const nonRetryableStatusCodes = [3100, 3200, 3300];
  return (
    nonRetryableStatusCodes.includes(error.data.supplementalStatusCode) ||
    error.status === 429
  );
};

/**
 * Render a button to send a temporary PIN when a temp PIN is not sent to the NH before. Otherwise, render a view
 * that informs NH that a temp PIN has been sent to their personal email address.
 */
const SendTempPinStepView = ({
  tempPinError,
  tempPinLoading,
  tempPinSent,
  sendTempPin,
}: SendTempPinStepViewProps): JSX.Element => {
  const [timesResent, setTimesResent] = useState(0);
  const { dispatch } = useContext(AppContext);

  /**
   * On Click handler for handling resend PIN link clicks.
   */
  const handleResendPin = (): void => {
    // Keep track of the number of times they click resend.
    const resentCounter = timesResent + 1;
    setTimesResent(resentCounter);

    // Create journey metrics for clicking resend.
    trackStructEvent({
      category: Day1EventCategory.MidwayRegisterSecKey,
      action: Day1EventAction.ResendTempPin,
      value: resentCounter,
    });

    sendTempPin({});
  };

  /**
   * Send temporary pin to NH's personal email address.
   */
  const handleSendTempPin = (): void => {
    // Track journey metrics for when they click the send pin button.
    trackStructEvent({
      category: Day1EventCategory.MidwayRegisterSecKey,
      action: Day1EventAction.SendTempPin,
    });

    dispatch({
      type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
      nonDirectedChatSupportContext: "ON_MIDWAY_PIN_SENT",
    });

    // The mutate function from the UseMutationResult always requires a variable. Passing in an empty object to avoid
    // getting an error from eslint.
    sendTempPin({});
  };

  /**
   * Returns view that informs NH that a temp PIN has been sent to their personal email address.
   * If there is an error when resending the pin, an error banner will be displayed informing the user to try again later.
   */
  const SendPinSuccessMessage: JSX.Element = (
    <>
      {tempPinError && <SendPinError error={tempPinError} />}
      <View backgroundColor={ITS_LIGHT_TEAL} padding="S400">
        <StepBodyStyledText>
          <Text fontWeight="normal" dataTestId="pinSentConfirmText">
            <FormattedMessage
              id="registerSecurityKey-expander-body-pinSentConfirmation"
              values={{
                resendLink: (
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    display="inline"
                    color="#49a4b6"
                    textDecoration={LinkTextDecorationOptions.None}
                    dataTestId="pinResendLink"
                    onClick={handleResendPin}
                    className="wizard-link"
                  >
                    <Row gridGap="S200">
                      <Text fontWeight="normal" display="inline">
                        <FormattedMessage id="registerSecurityKey-link-resend" />
                      </Text>
                      {tempPinLoading && (
                        <Spinner dataTestId="next-step-spinner" />
                      )}
                    </Row>
                  </Link>
                ),
              }}
            />
          </Text>
        </StepBodyStyledText>
      </View>
    </>
  );

  /**
   * Returns the send temporary pin button.
   * A spinner and loading text will show while the pin is sending and the button will be disabled.
   * If there is an error when sending the pin, an error banner will be displayed informing the user to try again later.
   */
  const SendPinButton: JSX.Element = (
    <>
      {tempPinError && <SendPinError error={tempPinError} />}
      <Row>
        <Button
          onClick={handleSendTempPin}
          dataTestId="sendTempPinBtn"
          disabled={tempPinLoading}
        >
          {tempPinLoading ? (
            <Row gridGap="S200">
              <Spinner dataTestId="next-step-spinner" />
              <Text>
                <FormattedMessage id="registerSecurityKey-button-sendTempPin-loadingText" />
              </Text>
            </Row>
          ) : (
            <Text>
              <FormattedMessage id="registerSecurityKey-button-sendTempPin" />
            </Text>
          )}
        </Button>
      </Row>
    </>
  );

  if (tempPinSent || timesResent > 0) {
    return SendPinSuccessMessage;
  }

  if (isNonRetryableError(tempPinError)) {
    return <>{tempPinError && <SendPinError error={tempPinError} />}</>;
  }

  return SendPinButton;
};

/**
 * A step to help NHs to register their security keys.
 */
export default function RegisterSecurityKey(): JSX.Element {
  const { state, dispatch } = useContext(AppContext);
  const [midwayPopupWindow, setMidwayPopupWindow] = useState<Window | null>(
    null
  );
  const intl = useIntl();
  const stepPadding = { bottom: "S200" };
  const {
    error,
    isLoading: tempPinLoading,
    isSuccess: tempPinSent,
    mutate: sendTempPin,
  } = useSendTempPin();
  const isMac = getOS() === OPERATING_SYSTEM.MAC;

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    if (tempPinSent) {
      dispatch({
        type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
        chatSupportRefPage: "ezo-midway-register-sec-key-after-temp-pin",
      });
    } else if (error) {
      if (error.response && error.response.data) {
        const midwayError = error.response.data;
        switch (midwayError.supplementalStatusCode) {
          case 3100:
            dispatch({
              type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
              chatSupportRefPage: "ezo-mw-error-100",
            });
            break;
          case 3200:
            dispatch({
              type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
              chatSupportRefPage: "ezo-mw-error-200",
            });
            break;
          case 3300:
            dispatch({
              type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
              chatSupportRefPage: "ezo-mw-error-300",
            });
            break;
          default:
            dispatch({
              type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
              chatSupportRefPage: "ezo-mw-error-999",
            });
            break;
        }
      }
    } else {
      dispatch({
        type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
        chatSupportRefPage: "ezo-midway-register-sec-key",
      });
    }
  }, [tempPinSent, error]);

  /**
   * Emit a success event when Midway temp PIN mutation succeeds
   */
  useEffect(() => {
    if (tempPinSent) {
      trackStructEvent({
        category: Day1EventCategory.MidwayRegisterSecKey,
        action: Day1EventAction.SendTempPinResponse,
        label: Day1EventLabel.MIDWAY_SUCCESS,
      });
    }
  }, [tempPinSent]);

  /**
   * Emit an error event when the Midway temp PIN mutation returns an error
   */
  useEffect(() => {
    if (error) {
      // this should always be true as long as the errors are coming from our API
      if (error.response && error.response.data) {
        const midwayError = error.response.data;
        switch (midwayError.supplementalStatusCode) {
          case 3100:
            dispatch({
              type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
              nonDirectedChatSupportContext: "ON_MIDWAY_ERROR_100",
            });
            trackStructEvent({
              category: Day1EventCategory.MidwayRegisterSecKey,
              action: Day1EventAction.SendTempPinResponse,
              label: Day1EventLabel.MIDWAY_ERROR_100,
            });
            break;
          case 3200:
            dispatch({
              type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
              nonDirectedChatSupportContext: "ON_MIDWAY_ERROR_200",
            });
            trackStructEvent({
              category: Day1EventCategory.MidwayRegisterSecKey,
              action: Day1EventAction.SendTempPinResponse,
              label: Day1EventLabel.MIDWAY_ERROR_200,
            });
            break;
          case 3300:
            dispatch({
              type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
              nonDirectedChatSupportContext: "ON_MIDWAY_ERROR_300",
            });
            trackStructEvent({
              category: Day1EventCategory.MidwayRegisterSecKey,
              action: Day1EventAction.SendTempPinResponse,
              label: Day1EventLabel.MIDWAY_ERROR_300,
            });
            break;
          default:
            dispatch({
              type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
              nonDirectedChatSupportContext: "ON_MIDWAY_ERROR_999",
            });
            trackStructEvent({
              category: Day1EventCategory.MidwayRegisterSecKey,
              action: Day1EventAction.SendTempPinResponse,
              label: Day1EventLabel.MIDWAY_ERROR_999,
            });
            break;
        }
      } else {
        // this should be a network issue, was already logged in useSendTempPin
      }
    }
  }, [error]);

  /**
   * Close open popup when moving on to next step
   */
  /* istanbul ignore next: will cover in integration tests */
  useEffect(() => {
    if (midwayPopupWindow) {
      midwayPopupWindow.close();
      setMidwayPopupWindow(null);
    }
  }, [state.currentStepNum]);

  if (!state.amazonUsername) {
    return <AccountIssue refPage="ezo-blocking-error-200" />;
  }

  const setConfirmRegistration = (checked: boolean) => {
    trackStructEvent({
      category: Day1EventCategory.MidwayRegisterSecKey,
      action: checked
        ? Day1EventAction.CheckedConfirmation
        : Day1EventAction.UncheckedConfirmation,
    });

    dispatch({
      stepId: STEP_ID.REGISTER_SECURITY_KEY,
      type: ActionType.SET_NEXT_BTN_STATUS,
      enableNext: checked,
    });
  };

  /**
   * Open a PopUp window that shows Midway security key registration portal in a side-by-side view.
   * TODO: Add integration tests to cover this. https://sim.amazon.com/issues/NHE-243
   */
  /* istanbul ignore next */
  const openMidWayPopup = () => {
    if (!midwayPopupWindow) {
      const ezoWidthPercentage = 0.4;
      // Open a popup 60% width of the screen wide, right justified.
      const midwayWindowRef: Window | null = createSideBySidePopup({
        url: "https://regportal.midway-auth.amazon.com",
        ezoWidthPercentage: ezoWidthPercentage,
        windowPopupWidthPercentage: 1 - ezoWidthPercentage,
      });

      // Resize wizard to 1/3 width and move to top left corner.
      resizeWizardForSideBySide({ ezoWidthPercentage: ezoWidthPercentage });
      midwayWindowRef?.focus();

      // Submit journey metrics for when the midway popup is opened.
      trackStructEvent({
        category: Day1EventCategory.MidwayRegisterSecKey,
        action: Day1EventAction.OpenMidwayPortal,
      });

      // Keep track of if the window has been closed yet or not so we can resize the main window after its been closed.
      // Note that adding an event listener for `close` and reassigning midwayWindowRef.onclose don't work.
      const i = setInterval(() => {
        if (midwayWindowRef?.closed) {
          // clear timer.
          clearInterval(i);

          // resize wizard to full screen.
          window.resizeTo(window.screen.availWidth, window.screen.availHeight);

          // Dereference if the window is closed so that they can open a new window later.
          setMidwayPopupWindow(null);

          // Track journey metrics for midway window closed.
          trackStructEvent({
            category: Day1EventCategory.MidwayRegisterSecKey,
            action: Day1EventAction.CloseMidwayPortalManual,
          });
        }
      }, 1_000);
      setMidwayPopupWindow(midwayWindowRef);
    } else {
      // Window is still open, focus on it.
      midwayPopupWindow?.focus();
    }
  };

  return (
    <Col gridGap="S400">
      <Col gridGap="S200">
        <Video
          videoFileConfig={
            VIDEOS_PLAYBACK_CONFIGS[VideoGuidanceName.SECURITY_KEY_OVERVIEW]
          }
        />
        <StepBodyStyledText>
          <H3 dataTestId="regSecKeyTitle">
            <FormattedMessage id="registerSecurityKey-expander-body-securityKeyTitle" />
          </H3>
        </StepBodyStyledText>
        <Col gridGap="S400">
          <StepBodyStyledText>
            <Text fontWeight="normal" dataTestId="regSecKeyDescription1">
              <FormattedMessage id="registerSecurityKey-expander-body-securityKeyDescription1" />
            </Text>
          </StepBodyStyledText>
          <StepBodyStyledText>
            <Text fontWeight="normal" dataTestId="regSecKeyDescription2">
              <FormattedMessage id="registerSecurityKey-expander-body-securityKeyDescription2" />
            </Text>
          </StepBodyStyledText>
        </Col>
      </Col>
      <Col gridGap="S200">
        <StepBodyStyledText>
          <H3 dataTestId="midwayTitle">
            <FormattedMessage id="registerSecurityKey-expander-body-midwayTitle" />
          </H3>
        </StepBodyStyledText>
        <StepBodyStyledText>
          <Text fontWeight="normal" dataTestId="midwayDescription">
            <FormattedMessage id="registerSecurityKey-expander-body-midwayDescription" />
          </Text>
        </StepBodyStyledText>
      </Col>
      <Col>
        <StepBodyStyledText>
          <H3 dataTestId="regSecKeyHowTitle">
            <FormattedMessage id="registerSecurityKey-expander-body-howTitle" />
          </H3>
        </StepBodyStyledText>
        <StepBodyStyledText>
          <Text fontWeight="normal">
            <ol className="reg-security-key-ol">
              <Col padding={stepPadding}>
                <li>
                  <Col gridGap="S300">
                    <Text fontWeight="normal" dataTestId="regSecKeyStep1">
                      <FormattedMessage id="registerSecurityKey-expander-body-step1" />
                    </Text>
                    <SendTempPinStepView
                      tempPinError={error?.response}
                      tempPinLoading={tempPinLoading}
                      tempPinSent={tempPinSent}
                      sendTempPin={sendTempPin}
                    />
                  </Col>
                </li>
              </Col>
              <Col padding={{ ...stepPadding, top: "S300" }}>
                <li>
                  <Col gridGap="S300">
                    <Text fontWeight="normal" dataTestId="regSecKeyStep2">
                      <FormattedMessage id="registerSecurityKey-expander-body-step2" />
                    </Text>
                    <Row>
                      <Button
                        icon={
                          <IconExternalLinkSmall
                            title={intl.formatMessage({
                              id: "registerSecurityKey-button-registerSecurityKey",
                            })}
                          />
                        }
                        iconPosition={ButtonIconPosition.Trailing}
                        onClick={openMidWayPopup}
                        dataTestId="openMidWayBtn"
                      >
                        <Text>
                          <FormattedMessage id="registerSecurityKey-button-registerSecurityKey" />
                        </Text>
                      </Button>
                    </Row>
                  </Col>
                </li>
              </Col>
              <Col padding={stepPadding}>
                <li>
                  <Col gridGap="S300">
                    <Row gridGap="S100">
                      <Text fontWeight="normal" dataTestId="regSecKeyStep3">
                        <FormattedMessage id="registerSecurityKey-expander-body-step3" />
                      </Text>
                    </Row>
                  </Col>
                  <Col>
                    <ul>
                      <Row>
                        <li>
                          <Text
                            fontWeight="normal"
                            dataTestId="regSecKeyStep3.1"
                          >
                            <FormattedMessage id="registerSecurityKey-expander-body-step3-1" />
                          </Text>
                        </li>
                      </Row>
                      <Row>
                        <li>
                          <Text
                            fontWeight="normal"
                            dataTestId="regSecKeyStep3.2"
                          >
                            <FormattedMessage id="registerSecurityKey-expander-body-step3-2" />
                          </Text>
                        </li>
                      </Row>
                    </ul>
                  </Col>
                </li>
              </Col>
              <Col padding={stepPadding}>
                <li>
                  <Row gridGap="S100">
                    <Text fontWeight="normal" dataTestId="regSecKeyStep4">
                      <FormattedMessage id="registerSecurityKey-expander-body-step4" />
                    </Text>
                  </Row>
                </li>
              </Col>
              <Col padding={stepPadding}>
                <ImagesRow
                  images={[
                    {
                      src: tempPinLogin,
                      altTextLocaleId:
                        "registerSecurityKey-image-tempPinLogin-altText",
                      widthPx: 327,
                      heightPx: 251,
                    },
                  ]}
                  rowPadding="S700"
                />
              </Col>
              <Col padding={stepPadding}>
                <li>
                  <Row gridGap="S100">
                    <Text fontWeight="normal" dataTestId="regSecKeyStep5">
                      <FormattedMessage id="registerSecurityKey-expander-body-step5" />
                    </Text>
                  </Row>
                </li>
              </Col>
              <Col padding={stepPadding}>
                <ImagesRow
                  images={[
                    {
                      src: midwayWelcomeMessage,
                      altTextLocaleId:
                        "registerSecurityKey-image-midwayWelcomeMessage-altText",
                      widthPx: 576,
                      heightPx: 102,
                    },
                  ]}
                  rowPadding="S700"
                />
              </Col>
              <Col padding={stepPadding}>
                <li>
                  <Row gridGap="S100">
                    <Text fontWeight="normal" dataTestId="regSecKeyStep6">
                      <FormattedMessage id="registerSecurityKey-expander-body-step6" />
                    </Text>
                  </Row>
                </li>
              </Col>
              <Col padding={stepPadding}>
                <ImagesRow
                  images={[
                    {
                      src: securityKeyCreateNewPin,
                      altTextLocaleId:
                        "registerSecurityKey-image-securityKeyCreateNewPin-altText",
                      widthPx: 576,
                      heightPx: 102,
                    },
                  ]}
                  rowPadding="S700"
                />
              </Col>
              {isMac && (
                <>
                  <Col padding={stepPadding}>
                    <li>
                      <Row gridGap="S100">
                        <Text
                          fontWeight="normal"
                          dataTestId="regSecKeyPreStep7"
                        >
                          <FormattedMessage id="registerSecurityKey-expander-pre-step-7-mac" />
                        </Text>
                      </Row>
                    </li>
                  </Col>
                  <Col padding={stepPadding}>
                    <ImagesRow
                      images={[
                        {
                          src: securityKeyKeyboardSetup,
                          altTextLocaleId:
                            "registerSecurityKey-image-securityKeyKeyboardSetup-altText",
                          widthPx: 576,
                          heightPx: 102,
                        },
                      ]}
                      rowPadding="S700"
                    />
                  </Col>
                </>
              )}
              <Col padding={stepPadding}>
                <li>
                  <Row gridGap="S100">
                    <Text fontWeight="normal" dataTestId="regSecKeyStep7">
                      <FormattedMessage id="registerSecurityKey-expander-body-step7" />
                    </Text>
                  </Row>
                </li>
              </Col>
              <Col padding={stepPadding}>
                <ImagesRow
                  images={[
                    {
                      src: securityKeyGetStartedOtp,
                      altTextLocaleId:
                        "registerSecurityKey-image-securityKeyGetStartedOtp-altText",
                      widthPx: 576,
                      heightPx: 102,
                    },
                  ]}
                  rowPadding="S700"
                />
              </Col>

              <Col padding={stepPadding}>
                <li>
                  <Row gridGap="S100">
                    <Text fontWeight="normal" dataTestId="regSecKeyStep8">
                      <FormattedMessage id="registerSecurityKey-expander-body-step8" />
                    </Text>
                  </Row>
                </li>
              </Col>
              <Col padding={stepPadding}>
                <ImagesRow
                  images={[
                    {
                      src: securityKeyRegisterKey,
                      altTextLocaleId:
                        "registerSecurityKey-image-securityKeyGetStarted-altText",
                      widthPx: 576,
                      heightPx: 102,
                    },
                  ]}
                  rowPadding="S700"
                />
              </Col>
              <Col padding={stepPadding}>
                <li>
                  <Row gridGap="S100">
                    <Text fontWeight="normal" dataTestId="regSecKeyStep9">
                      <FormattedMessage id="registerSecurityKey-expander-body-step9" />
                    </Text>
                  </Row>
                </li>
              </Col>
              <Col padding={stepPadding}>
                <ImagesRow
                  images={[
                    {
                      src: securityKeySuccess,
                      altTextLocaleId:
                        "registerSecurityKey-image-securityKeySuccess-altText",
                      widthPx: 576,
                      heightPx: 102,
                    },
                  ]}
                  rowPadding="S700"
                />
              </Col>
            </ol>
            <Col padding={{ top: "S200" }} gridGap="S300">
              <Row>
                <Text fontWeight="normal" dataTestId="regSecKeyNote">
                  <FormattedMessage id="registerSecurityKey-expander-body-note" />
                </Text>
              </Row>
              <Row alignItems="center" gridGap="S200">
                <Checkbox
                  colorScheme={CheckboxColorScheme.Default}
                  id="confirmSecurityKeyRegistrationCheckBox"
                  dataTestId="confirmSecurityKeyRegistrationCheckBox"
                  onChange={(evt) => setConfirmRegistration(evt.target.checked)}
                />
                <Label htmlFor="confirmSecurityKeyRegistrationCheckBox">
                  <Text
                    fontWeight="normal"
                    dataTestId="regSecKeyConfirmationText"
                  >
                    <FormattedMessage id="registerSecurityKey-checkbox-confirmation" />
                  </Text>
                </Label>
              </Row>
            </Col>
          </Text>
        </StepBodyStyledText>
      </Col>
    </Col>
  );
}
